import { USD, CoinType, DASBalanceTokenId } from '@did/constants/chain'
import { useConfigVal, useDasBalanceContext } from '@did/das-app-context'
import { IDidPointDepositRes, ITokenInfo } from '@did/types'
import { useState, useEffect, useMemo } from 'react'
import { useConfig } from './use-config'
import Decimal from 'decimal.js'
import { TOKEN_DECIMAL_PLACES } from '@did/constants'
import errno from '@did/constants/errno'
import { ErrorInfo } from '@did/tools'

export const useTopUp = () => {
  const { walletSdk, services, connectedAccount, router, alert, tt } =
    useDasBalanceContext()
  const [didPointAmount, setDidPointAmount] = useState('')
  const [continueLoading, setContinueLoading] = useState(false)
  const [payLoading, setPayLoading] = useState(false)
  const [selectPaymentShowing, setSelectPaymentShowing] = useState(false)
  const [
    dasBalanceInsufficientDialogShowing,
    setDasBalanceInsufficientDialogShowing
  ] = useState(false)

  const [signatureErrorDialogShowing, setSignatureErrorDialogShowing] =
    useState(false)

  const [fiatPayDialogShowing, setFiatPayDialogShowing] = useState(false)
  const [paymentTokens, setPaymentTokens] = useState<ITokenInfo[]>([])
  const [selectedToken, setSelectedToken] = useState<ITokenInfo>()
  const [orderInfo, setOrderInfo] = useState<IDidPointDepositRes>({
    amount: '',
    amount_big_int: '',
    client_secret: '',
    order_id: '',
    payment_address: ''
  })
  const [clientSecret, setClientSecret] = useState<string>()

  const { config } = useConfig()

  const commonConfig = useConfigVal({ services })

  const goRecordPage = (orderId: string) => {
    router.push('/credits/record', {
      orderId: orderId
    })
  }

  const getTokens = async () => {
    try {
      const res = await services.didPoint.tokenList()
      if (!(res && res.token_list && res.token_list.length > 0)) {
        return
      }
      setPaymentTokens(res.token_list)
    } catch (e: any) {
      ErrorInfo.error(e)
    }
  }

  useEffect(() => {
    getTokens()
  }, [])

  const amountVerify = (amount: number | string): string => {
    let number = amount
    // Compatible with TokenPocket
    if (selectedToken?.coin_type === CoinType.doge) {
      number = new Decimal(number || 0)
        .div(new Decimal(10).pow(TOKEN_DECIMAL_PLACES))
        .toFixed(TOKEN_DECIMAL_PLACES)
    }
    return number + ''
  }

  const onFiatPay = async () => {
    if (!connectedAccount || !selectedToken) return
    setPayLoading(true)

    try {
      const orderInfo = await services.didPoint.didPointDeposit({
        key_info: {
          coin_type: connectedAccount.chain?.coinType,
          key: connectedAccount.address
        },
        amount_dp: didPointAmount,
        token_id: selectedToken.token_id
      })
      setOrderInfo(orderInfo)

      if (orderInfo.client_secret) {
        setClientSecret(orderInfo.client_secret)
      }
      setSelectPaymentShowing(false)
      setFiatPayDialogShowing(true)
    } catch (err: any) {
      ErrorInfo.error(err)

      if (err.code === errno.apiErrorCodeResolveFailed) {
        alert({
          title: tt('Tips'),
          message: tt(
            'Frequent operations. There are still transactions being processed in your wallet address, please try again after 30s.'
          )
        })
      } else {
        alert({
          title: tt('Error'),
          message: err.code ? `${err.code}: ${err.message}` : `${err}`
        })
      }
    } finally {
      setPayLoading(false)
    }
  }

  const onPay = async () => {
    if (!selectedToken || !connectedAccount) return

    if (selectedToken.token_id === USD.tokenId) {
      onFiatPay()
      return
    }
    setPayLoading(true)
    const { signTxList, onClose } = await walletSdk?.initSignContext()

    try {
      const orderInfo = await services.didPoint.didPointDeposit({
        key_info: {
          coin_type: connectedAccount.chain?.coinType,
          key: connectedAccount.address
        },
        amount_dp: didPointAmount,
        token_id: selectedToken.token_id
      })
      setOrderInfo(orderInfo)

      if (selectedToken.token_id === DASBalanceTokenId) {
        const res = await services.didPoint.dasBalancePay({
          evm_chain_id: connectedAccount.computedChainId,
          key_info: {
            coin_type: connectedAccount.chain?.coinType,
            key: connectedAccount.address
          },
          order_id: orderInfo?.order_id!
        })

        const signatureList = await signTxList(res)

        const { hash } = await services.didPoint.txSend(signatureList)
        if (hash) {
          await services.didPoint.returnPaymentHash({
            key_info: {
              coin_type: connectedAccount.chain?.coinType,
              key: connectedAccount.address
            },
            order_id: orderInfo?.order_id!,
            payment_hash: hash
          })
          goRecordPage(orderInfo.order_id)
          setSelectPaymentShowing(false)
        }
      } else {
        const trxAmount = amountVerify(orderInfo.amount_big_int)
        let trxId = await walletSdk.sendTransaction({
          to: orderInfo.payment_address,
          value: trxAmount,
          data: orderInfo.order_id
        })
        if (trxId && typeof trxId === 'string') {
          await services.didPoint.returnPaymentHash({
            key_info: {
              coin_type: connectedAccount.chain?.coinType,
              key: connectedAccount.address
            },
            order_id: orderInfo?.order_id!,
            payment_hash: trxId
          })

          goRecordPage(orderInfo.order_id)
          setSelectPaymentShowing(false)
        }
      }
    } catch (err: any) {
      ErrorInfo.error(err)
      onClose?.()
      if (
        ![
          errno.metaMaskUserRejectedAccountAccess,
          errno.metaMaskUserDeniedMessageSignature
        ].includes(err.code) &&
        err !== errno.tronLinkConfirmationDeclinedByUser &&
        err.message !== errno.walletConnectUserRejectedTheTransaction &&
        err.shortMessage !== errno.walletConnectUserRejectedTheRequest
      ) {
        if (
          err.code === errno.metaMaskWalletRequestPermissions ||
          err.code === errno.tronLinkAuthorizationRequestsAreBeingProcessed
        ) {
          alert({
            title: tt('Tips'),
            message: tt(
              'Other requests for the wallet are not processed, please try again after processing'
            )
          })
        } else if (err.message === errno.coinbaseWalletUsingMultipleWallet) {
          alert({
            title: tt('Tips'),
            message: tt(
              'Please check if you are using multiple wallet plugins. Please disable multiple wallet plugins, keep only one wallet plugin and try again.'
            )
          })
        } else if (
          err === errno.tronLinkInsufficientBalance ||
          (err.message &&
            err.message.includes(
              errno.walletConnectInsufficientFundsForTransfer
            ))
        ) {
          alert({
            title: tt('Tips'),
            message: tt('Insufficient balance')
          })
        } else if (
          err.message &&
          err.message.includes(errno.tronLinkTypeErrorAddUpdateDataNotFunction)
        ) {
          alert({
            title: tt('Tips'),
            message: tt(
              'The current wallet environment does not support payments using TRX, please upgrade your wallet version or register with another wallet.'
            )
          })
        } else if (err.code === errno.apiErrorCodeInsufficientBalance) {
          setDasBalanceInsufficientDialogShowing(true)
        } else if (err.code === errno.apiErrorCodeNotEnoughChange) {
          alert({
            title: tt('Tips'),
            message: tt(
              'DAS is a smart contract that runs on the Nervos. Due to the underlying logic of the contract, the remaining amount is too low (less than {minChangeCapacity} CKB) to send a transaction.',
              {
                minChangeCapacity: commonConfig.min_change_capacity
              }
            )
          })
        } else if (err.code === errno.apiErrorCodeResolveFailed) {
          alert({
            title: tt('Tips'),
            message: tt(
              'Frequent operations. There are still transactions being processed in your wallet address, please try again after 30s.'
            )
          })
        } else if (err.code === errno.rpcApiErrSignatureError) {
          setSignatureErrorDialogShowing(true)
        } else {
          alert({
            title: tt('Error'),
            message: err.code ? `${err.code}: ${err.message}` : `${err}`
          })
        }
      }
    } finally {
      setPayLoading(false)
    }
  }

  const onTopUp = async () => {
    try {
      if (continueLoading) {
        return
      }
      setContinueLoading(true)
      const isInit = await walletSdk?.initWallet()
      if (!isInit) {
        setContinueLoading(false)
        return
      }
      setSelectPaymentShowing(true)
    } catch (err: any) {
      ErrorInfo.error(err)
    } finally {
      setContinueLoading(false)
    }
  }

  const stripeServiceFee = useMemo(() => {
    if (!selectedToken?.decimals) return ''

    return new Decimal(didPointAmount || 0)
      .times(config?.premium_percentage || 0)
      .add(config?.premium_base || 0)
      .toFixed(selectedToken?.decimals, Decimal.ROUND_UP)
  }, [selectedToken, didPointAmount, config])

  const stripePaidAmount = useMemo(() => {
    if (!selectedToken?.decimals) return ''

    return new Decimal(didPointAmount || 0)
      .add(stripeServiceFee)
      .toFixed(selectedToken?.decimals, Decimal.ROUND_UP)
  }, [didPointAmount, stripeServiceFee, selectedToken])

  const paidTokenAmount = useMemo(() => {
    return new Decimal(didPointAmount || 0)
      .div(selectedToken?.price || 1)
      .toFixed(
        selectedToken?.token_id === USD.tokenId
          ? selectedToken?.decimals
          : TOKEN_DECIMAL_PLACES,
        Decimal.ROUND_UP
      )
  }, [didPointAmount, selectedToken])

  return {
    onTopUp,
    selectPaymentShowing,
    setSelectPaymentShowing,
    paymentTokens,
    selectedToken,
    setSelectedToken,
    stripeServiceFee,
    stripePaidAmount,
    paidTokenAmount,
    onPay,
    payLoading,
    dasBalanceInsufficientDialogShowing,
    setDasBalanceInsufficientDialogShowing,
    signatureErrorDialogShowing,
    setSignatureErrorDialogShowing,
    fiatPayDialogShowing,
    setFiatPayDialogShowing,
    continueLoading,
    setDidPointAmount,
    clientSecret,
    goRecordPage,
    orderId: orderInfo.order_id
  }
}
